<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-6">
					<h3 class="page-header">
						<i class="fa fa-check-square-o animated bounceInDown show-info"></i>
						Attendance Approvals
					</h3>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-4">
					<label>Status</label>
					<select class="form-control form-cascade-control input-box-short" v-model="status" @change="refresh">
						<option value="pending">Pending</option>
						<option value="approved">Approved</option>
						<option value="rejected">Rejected</option>
					</select>
				</div>
				<div class="col-md-4">
					<label>Module</label>
					<select class="form-control form-cascade-control input-box-short" v-model="moduleFilter" @change="refresh">
						<option value="">All</option>
						<option value="leave">Leave</option>
						<option value="overtime">Overtime</option>
						<option value="regularization">Correction</option>
						<option value="short_leave">Short leave</option>
						<option value="geo_exception">Out of area</option>
					</select>
				</div>
				<div class="col-md-4 text-right">
					<label class="d-block">&nbsp;</label>
					<button class="btn btn-primary" :disabled="runningAuto" @click="runAuto">
						<i class="fa fa-bolt"></i> Run auto-approvals
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>
		<div class="col-md-12 text-center" v-else-if="loading" style="margin-top:40px">
			<img src="../../assets/loading.gif" style="width:48px" alt="Loading" />
		</div>
		<div class="col-md-12 text-center text-muted" v-else-if="!requests.length" style="margin-top:40px">
			Nothing here. All caught up.
		</div>

		<div class="col-md-12" v-else style="margin-top:10px">
			<div class="row">
				<div class="col-md-6" v-for="request in requests" :key="request.id" style="margin-bottom:16px">
					<div class="panel approval-card">
						<div class="panel-body">
							<div class="flex-row" style="display:flex;justify-content:space-between">
								<div style="min-width:0">
									<div style="margin-bottom:8px">
										<span class="badge" :class="moduleBadgeClass(request.module)">
											{{ moduleLabel(request.module) }}
										</span>
										<strong style="margin-left:6px">{{ request.employee.name || 'Unknown' }}</strong>
										<small class="text-muted" v-if="request.employee.code">
											{{ request.employee.code }}
										</small>
									</div>
									<p class="font-weight-bold" style="margin-bottom:2px">
										{{ request.detail.title || moduleLabel(request.module) }}
									</p>
									<p class="text-muted" style="margin-bottom:2px">{{ request.detail.subtitle }}</p>
									<p v-if="request.detail.reason" class="text-muted font-italic" style="margin-top:6px">
										"{{ request.detail.reason }}"
									</p>
									<p v-if="request.detail.address" class="text-muted" style="margin-top:4px">
										<i class="fa fa-map-marker"></i> {{ request.detail.address }}
									</p>
									<a
										v-if="request.detail.document_url"
										:href="request.detail.document_url"
										target="_blank"
										rel="noopener"
										class="text-primary"
									>
										View attachment
									</a>
									<p class="text-muted" style="margin-top:8px;font-size:12px">
										Requested {{ formatDateTime(request.requested_at) }}
										<span v-if="request.total_levels > 1">
											· level {{ request.current_level }} of {{ request.total_levels }}
										</span>
									</p>
								</div>
								<img
									v-if="request.detail.selfie_url"
									:src="request.detail.selfie_url"
									alt="Punch selfie"
									class="approval-selfie"
								/>
							</div>

							<div v-if="request.status === 'pending'" style="margin-top:14px">
								<button class="btn btn-sm btn-primary" @click="open(request, true)">
									<i class="fa fa-check"></i> Approve
								</button>
								<button class="btn btn-sm btn-danger" @click="open(request, false)">
									<i class="fa fa-times"></i> Reject
								</button>
							</div>
							<span v-else class="badge bg-secondary" style="margin-top:14px;display:inline-block">
								{{ request.status }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<modal name="attendance-decision" :width="440" :height="'auto'" :adaptive="true">
			<div style="padding:18px" v-if="target">
				<h4 class="modal-header-title">{{ approving ? 'Approve request' : 'Reject request' }}</h4>
				<p class="text-muted">
					{{ target.detail.title }} — {{ target.employee.name }}.
					<template v-if="approving">Approving recomputes the affected days straight away.</template>
				</p>
				<div class="form-group">
					<textarea class="form-control form-cascade-control" rows="3" placeholder="Note (optional)" v-model="note"></textarea>
				</div>
				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-decision')">Cancel</button>
					<button
						class="btn"
						:class="approving ? 'btn-primary' : 'btn-danger'"
						:disabled="saving"
						@click="submit"
					>
						{{ approving ? 'Approve' : 'Reject' }}
					</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { approvalService, errorMessage } from './attendanceService'
import attendanceFormat from './attendanceFormat'

export default {
	name: 'AttendanceApprovals',
	mixins: [attendanceFormat],
	data() {
		return {
			status: 'pending',
			moduleFilter: '',
			requests: [],
			loading: false,
			error: '',
			runningAuto: false,
			// Decision
			target: null,
			approving: true,
			note: '',
			saving: false,
		}
	},
	mounted() {
		this.refresh()
	},
	methods: {
		refresh() {
			this.loading = true
			this.error = ''
			approvalService
				.list({ status: this.status, module: this.moduleFilter || undefined })
				.then((data) => {
					this.requests = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load approvals.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		open(request, approve) {
			this.target = request
			this.approving = approve
			this.note = ''
			this.$modal.show('attendance-decision')
		},
		submit() {
			if (!this.target) return
			this.saving = true
			approvalService
				.decide(this.target.id, this.approving, this.note || undefined)
				.then(() => {
					this.$notify({
						text: `${this.approving ? 'Approved' : 'Rejected'}: the affected days have been recomputed.`,
						type: 'success',
					})
					this.$modal.hide('attendance-decision')
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		runAuto() {
			this.runningAuto = true
			approvalService
				.runAuto()
				.then((result) => {
					this.$notify({
						text: `Auto-approvals run: ${result.approved} request(s) pushed through.`,
						type: 'success',
					})
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
				.finally(() => {
					this.runningAuto = false
				})
		},
		moduleBadgeClass(module) {
			switch (module) {
				case 'leave':
					return 'bg-info text-dark'
				case 'overtime':
					return 'bg-warning text-dark'
				case 'geo_exception':
					return 'bg-danger'
				default:
					return 'bg-secondary'
			}
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
.approval-card {
	height: 100%;
}
.approval-selfie {
	width: 80px;
	height: 80px;
	border-radius: 8px;
	object-fit: cover;
	margin-left: 10px;
	flex-shrink: 0;
}
</style>
